import moment from 'moment';

import { Box, Flex, Text, UserGroupBadge } from '@frontend/components';
import { useDateFormat } from '@frontend/utils';

import QuoteStatusBadge from 'components/lib2/QuoteStatusBadge';

import { useAppSelector } from 'store/hooks';
import { getActiveGroup } from 'store/session/selectors';

import { useUserGroupBadgeLookup } from 'utils/multisite';
import { useUserGroupCollectionContext } from 'utils/multisite/UserGroupCollectionProvider';

import HighlightOrDisplay from './HighlightOrDisplay';
import LabelText from './LabelText';
import ValueText from './ValueText';

const PaddedCell = ({ children }: { children?: React.ReactNode }) => (
  <td style={{ paddingRight: '8px' }}>{children}</td>
);

const QuoteMatchCell = ({
  quoteResult,
}: {
  quoteResult: { data: Record<string, any>; highlight: any };
}) => {
  const { dateAtTimeFormat } = useDateFormat();
  const getBadgeProps = useUserGroupBadgeLookup();
  const badgeProps = getBadgeProps(quoteResult.data.ownerId);

  const href = `/quotes/edit/${quoteResult.data.number}`;

  const activeGroup = useAppSelector(getActiveGroup);
  const isFromOutsideGroup = quoteResult.data.ownerId !== activeGroup.id;
  const { initChangeSite } = useUserGroupCollectionContext();

  const linkProps = isFromOutsideGroup
    ? {
        onClick: () =>
          initChangeSite({
            nextUrl: href,
            userGroupId: quoteResult.data.ownerId,
          }),
      }
    : { href };

  return (
    <Box
      as="a"
      display="block"
      width="100%"
      height="100%"
      transition="0.2s ease"
      _hover={{
        bg: 'interaction.surfaceHover',
      }}
      _focus={{
        boxShadow: 'focusInset',
      }}
      _active={{
        bg: 'interaction.surfaceActive',
      }}
      padding={1}
      {...linkProps}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingBottom={0.5}
        minWidth={0}
        width="100%"
      >
        <Flex alignItems="center" flexShrink={0}>
          <Text
            fontFamily="data"
            fontSize="sm"
            fontWeight="heavy"
            color="feature.primary"
          >
            {`Quote #`}
            <HighlightOrDisplay
              result={quoteResult}
              field="number"
              textProps={{ fontWeight: 'heavy' }}
              useDataFont
            />
          </Text>
          <QuoteStatusBadge
            flexShrink={0}
            marginLeft={0.5}
            size="sm"
            quoteStatus={quoteResult.data.status}
          />
        </Flex>
        {badgeProps && (
          <Box
            flex={1}
            minWidth={0}
            marginLeft={1}
            display="flex"
            justifyContent="flex-end"
            overflow="hidden"
          >
            <UserGroupBadge
              maxWidth="100%"
              size="sm"
              name={badgeProps.name}
              colorPalette={badgeProps.colorPalette}
              icon={badgeProps.icon}
            />
          </Box>
        )}
      </Flex>
      <Box as="table" borderCollapse="collapse">
        <tbody>
          <tr>
            <PaddedCell>
              <LabelText>CONTACT</LabelText>
            </PaddedCell>
            <td>
              <HighlightOrDisplay result={quoteResult} field="accountName" />
            </td>
          </tr>
          <tr>
            <PaddedCell />
            <td>
              <HighlightOrDisplay
                result={quoteResult}
                field="contactFullName"
              />
            </td>
          </tr>
          <tr>
            <PaddedCell />
            <td>
              <HighlightOrDisplay result={quoteResult} field="contactEmail" />
            </td>
          </tr>
          <tr>
            <PaddedCell>
              <LabelText>PRICING</LabelText>
            </PaddedCell>
            <td>
              <ValueText
                useDataFont
              >{`$${quoteResult.data.minimumTotal} - $${quoteResult.data.maximumSubtotal}`}</ValueText>
            </td>
          </tr>
          <tr>
            <PaddedCell>
              <LabelText>CREATED</LabelText>
            </PaddedCell>
            <td>
              <ValueText useDataFont>
                {quoteResult.data.created
                  ? moment(quoteResult.data.created)
                      .subtract(new Date().getTimezoneOffset(), 'minutes')
                      .format(dateAtTimeFormat)
                  : '--'}
              </ValueText>
            </td>
          </tr>
          <tr>
            <PaddedCell>
              <LabelText>DUE</LabelText>
            </PaddedCell>
            <td>
              <ValueText useDataFont>
                {quoteResult.data.dueDate
                  ? moment(quoteResult.data.dueDate)
                      .subtract(new Date().getTimezoneOffset(), 'minutes')
                      .format(dateAtTimeFormat)
                  : '--'}
              </ValueText>
            </td>
          </tr>
          <tr>
            <PaddedCell>
              <LabelText>EXPIRES</LabelText>
            </PaddedCell>
            <td>
              <ValueText useDataFont>
                {quoteResult.data.expiredDate
                  ? moment(quoteResult.data.expiredDate)
                      .subtract(new Date().getTimezoneOffset(), 'minutes')
                      .format(dateAtTimeFormat)
                  : '--'}
              </ValueText>
            </td>
          </tr>
        </tbody>
      </Box>
    </Box>
  );
};

export default QuoteMatchCell;
